import {
  BookmarkSimpleIcon,
  CheckCircleIcon,
  CheckIcon,
  FastForwardIcon,
  FlagBannerIcon,
  PlayIcon,
  SmileyXEyesIcon,
  TimerIcon,
  XCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import TableHeader from '../../components/generic/TableHeader';
import { useEffect, useState, useRef } from 'react';
import { cachedGet } from '../../lib/requestDedupe';
import QuestionJourneyPill from './QuestionJourneyPill';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';

export default function QuestionJourney({ packId, testId, testIdTwo, isCombined = false, totalTime, testsData }) {
  const [data, setData] = useState(null);
  const [showAttemptAnalysis, setShowAttemptAnalysis] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [timeFrames, setTimeframes] = useState([]);
  const [lastInRowIndices, setLastInRowIndices] = useState({});
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [correctFirstIncorrectLaterSet, setCorrectFirstIncorrectLaterSet] = useState(null);
  const containerRefs = useRef({});

  const [selectedTestIndex, setSelectedTestIndex] = useState(0);
  const [currentTestId, setCurrentTestId] = useState(testId);
  const [tests, setTests] = useState([]);
  const [currentTotalTime, setCurrentTotalTime] = useState(totalTime);

  useEffect(() => {
    if (testsData && Array.isArray(testsData) && testsData.length > 0) {
      setTests(testsData);
      setCurrentTestId(testsData[0]?.testId);
      setCurrentTotalTime(testsData[0]?.totalTime || totalTime);
      setSelectedTestIndex(0);
    } else {
      setCurrentTestId(testId);
      setCurrentTotalTime(totalTime);
    }
  }, [testsData, testId, totalTime]);

  const handleTestChange = (index) => {
    setSelectedTestIndex(index);
    setCurrentTestId(tests[index]?.testId);
    setCurrentTotalTime(tests[index]?.totalTime || totalTime);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const res = await cachedGet(`/api/test/analysis/v2/${packId}/${currentTestId}/questionJourney`);
        let response = res?.data?.data;
        const totalRequiredJourneys = Math.ceil(currentTotalTime / 30);

        if (response?.length > totalRequiredJourneys) {
          const merged = response.slice(0, totalRequiredJourneys);
          const extra = response.slice(totalRequiredJourneys);
          // Flatten all extra arrays and merge into the last required array
          merged[totalRequiredJourneys - 1] = [...merged[totalRequiredJourneys - 1], ...extra.flat()];
          response = merged;
        }
        const numTimeframes = response.length;
        const generatedTimeframes = Array.from({ length: numTimeframes }, (_, i) => {
          const start = i * 30;
          const end = (i + 1) * 30;
          return `${start} - ${end} min`;
        });
        setTimeframes(generatedTimeframes);
        setData(response);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [packId, currentTestId, currentTotalTime]);

  useEffect(() => {
    if (!data) return;

    const updateLastInRowForAllSections = () => {
      const newLastInRowIndices = {};

      data.forEach((_, sectionIndex) => {
        const container = containerRefs.current[sectionIndex];
        if (!container || !data[sectionIndex]?.length) return;
        const children = Array.from(container.children);
        const lastIndices = new Set();
        let currentRowTop = children[0]?.offsetTop;
        let lastInCurrentRow = 0;

        children.forEach((child, idx) => {
          if (child.offsetTop > currentRowTop) {
            // New row started, previous element was last in its row
            lastIndices.add(lastInCurrentRow);
            currentRowTop = child.offsetTop;
          }
          lastInCurrentRow = idx;
        });
        // Add the very last element
        lastIndices.add(lastInCurrentRow);
        newLastInRowIndices[sectionIndex] = lastIndices;
      });

      setLastInRowIndices(newLastInRowIndices);
    };

    // Initial calculation
    setTimeout(updateLastInRowForAllSections, 100);
    // Update on window resize
    window.addEventListener('resize', updateLastInRowForAllSections);

    // * calculate previously correct and finally incorrect answers
    const answersMap = {};
    data?.forEach((item) => {
      item.forEach((question) => {
        const qNo = question.questionNumber;
        if (!answersMap[qNo]) {
          answersMap[qNo] = [];
        }
        answersMap[qNo].push(question);
      });
    });

    const correctFirstIncorrectLaterSet = new Set();
    Object.entries(answersMap).forEach(([qNo, answers]) => {
      let wasCorrect = false;
      let wasCorrectIndex = -1;
      let becameIncorrect = false;
      let becameIncorrectIndex = -1;

      answers.forEach((ans, index) => {
        if (ans.isCorrect) {
          wasCorrect = true;
          wasCorrectIndex = index;
        } else if (!ans.isCorrect && (ans.selectedOptions?.length || ans.inputValue?.trim())) {
          becameIncorrect = true;
          becameIncorrectIndex = index;
        }
      });

      if (wasCorrect && becameIncorrect && wasCorrectIndex < becameIncorrectIndex) {
        correctFirstIncorrectLaterSet.add(Number(qNo));
      }
    });

    setCorrectFirstIncorrectLaterSet(correctFirstIncorrectLaterSet);

    return () => window.removeEventListener('resize', updateLastInRowForAllSections);
  }, [data]);

  const handleShowAttemptAnalysis = () => {
    setShowAttemptAnalysis(!showAttemptAnalysis);
  };

  const handleQuestionClick = (item) => {
    if (selectedQuestion === item?.questionNumber) {
      setSelectedQuestion(null);
      return;
    }
    setSelectedQuestion(item?.questionNumber);
  };

  if (isLoading) return <TestAnalysisLoader />;

  return (
    <div>
      {isCombined && tests.length > 0 && (
        <div className="flex gap-3 mb-4 pb-0">
          {tests.map((test, index) => (
            <button
              key={index}
              onClick={() => handleTestChange(index)}
              className={`px-3 py-2 font-medium text-sm transition-colors duration-200 border border-[var(--q3-stroke-normal)] rounded-4xl cursor-pointer ${
                selectedTestIndex === index
                  ? 'bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)]'
                  : 'text-[var(--q3-neutral-default)] bg-[var(--q3-surface-default)]'
              }`}
            >
              {test.testName}
            </button>
          ))}
        </div>
      )}

      <TableHeader
        headerTitle={'Qs Journey'}
        headerSwitch={'Show Quality of Attempt'}
        headerSwitchCase={showAttemptAnalysis}
        headerSwitchHandler={handleShowAttemptAnalysis}
      ></TableHeader>
      <div className="bg-[var(--q3-surface-dim)] p-4 border border-[var(--q3-stroke-light)] border-t-0 overflow-x-auto cursor-ew-resize select-none">
        <div className="flex flex-row items-center gap-4 min-w-max">
          {!showAttemptAnalysis ? (
            <>
              <div className="flex flex-row items-center gap-2">
                <CheckIcon className="text-[var(--q3-green-normal)]" size={20} weight="bold" />
                <p className="text-xs">Answered Correct</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <XIcon className="text-[var(--q3-danger-normal)]" size={20} weight="bold" />
                <p className="text-xs">Answered Wrong</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <FastForwardIcon className="text-[var(--q3-neutral-light)]" size={20} weight="fill" />
                <p className="text-xs">Skipped</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-row items-center gap-2">
                <CheckCircleIcon className="text-[var(--q3-green-normal)]" size={20} />
                <p className="text-xs">Perfect</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <XCircleIcon className="text-[var(--q3-danger-normal)]" size={20} />
                <p className="text-xs">Wasted</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <TimerIcon className="text-[var(--q3-orange-normal)]" size={20} />
                <p className="text-xs">Overtime</p>
              </div>
              <div className="flex flex-row items-center gap-2">
                <SmileyXEyesIcon className="text-[var(--q3-neutral-light)]" size={20} />
                <p className="text-xs">Confused</p>
              </div>
            </>
          )}
          <div className="flex flex-row items-center gap-2">
            <BookmarkSimpleIcon className="text-[var(--q3-accent-normal)]" size={20} weight="fill" />
            <p className="text-xs">Marked for Review</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <BookmarkSimpleIcon className="text-[var(--q3-accent-normal)]" size={20} weight="bold" />
            <p className="text-xs">Unmarked for Review</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="w-5 h-5 border border-[var(--q3-stroke-light)] rounded relative">
              <div className="w-5 h-5 flex items-center justify-center rounded-full border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] absolute top-0 -translate-x-1/2 -translate-y-1/2 text-xs text-[var(--q3-accent-normal)]">
                2
              </div>
            </div>
            <p className="text-xs">No of times you opened the question</p>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="w-5 h-5 border border-[var(--q3-danger-normal)] rounded relative" />
            <p className="text-xs">Wrong Attempt, Previously Correct</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col p-6 gap-10 border border-[var(--q3-stroke-light)] border-t-0 rounded-b-xl">
        {timeFrames?.map((timeFrame, index) => (
          <div key={index} className="mb-4">
            <div className="flex flex-row items-center gap-2 text-[var(--q3-neutral-default)] mb-2">
              {index > 0 ? <TimerIcon size={24} /> : <FlagBannerIcon size={24} />}
              <p className="font-bold">{index > 0 ? timeFrame : '0 - 30 min'}</p>
            </div>

            <div ref={(el) => (containerRefs.current[index] = el)} className="flex flex-wrap gap-10 mt-5">
              {data?.[index]?.map((item, qIndex) => (
                <div key={qIndex} className="relative">
                  <QuestionJourneyPill
                    item={item}
                    showAttemptAnalysis={showAttemptAnalysis}
                    selectedQuestion={selectedQuestion}
                    handleQuestionClick={handleQuestionClick}
                    correctFirstIncorrectLater={correctFirstIncorrectLaterSet?.has(item?.questionNumber)}
                  />
                  {!lastInRowIndices[index]?.has(qIndex) && (
                    <div className="absolute border-t border-dashed border-[var(--q3-stroke-dark)] top-1/2 -translate-y-1/2 translate-x-10 w-full" />
                  )}
                  {!lastInRowIndices[index]?.has(qIndex) && (
                    <div className="absolute top-[14px] right-[-25px] text-[var(--q3-stroke-dark)] text-sm">
                      <PlayIcon size={12} weight="fill" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
